import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';

import { useUserStore } from '@/configs/store';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { WeeklyWorkLog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { fromISOtoISODate } from '@/utils/fromISOtoISODate';

import { TimeLogForm } from './TimeLogForm';
import { DataValues } from './types';
import { getInitialUsers } from './utils';

type EditLogProps = {
  log: WeeklyWorkLog;
  open: boolean;
  onSubmit: (data: DataValues & { workLogId: string }) => Promise<void>;
  onClose: () => void;
  onDelete?: () => void;
  day?: DateTime | null;
};

const EditLog: React.FC<EditLogProps> = observer(({ log, onClose, open, onSubmit, onDelete }) => {
  const { user, isAdmin } = useUserStore();
  const {
    usersFilterModel: { selectedUserId, selectedUser, myUserId },
  } = useTimeLogStore();
  const { id, taskKey, taskTitle, durationMinutes, comment, start, originalStart } = log;

  const actualStart = originalStart ?? start;
  const logStartDate = fromISOtoISODate(actualStart);

  const usersWithMyself = getInitialUsers({
    user,
    myUserId,
    isAdmin,
    selectedUserId,
    selectedUser,
  });

  const logStartTime = DateTime.fromISO(actualStart).toFormat('HH:mm');

  const initialValues = {
    taskKey,
    taskLabel: `${taskKey} — ${taskTitle}`,
    comment: comment ?? '',
    users: usersWithMyself,
    dateEntries: [
      {
        startDate: logStartDate ?? '',
        duration: formatDurationRussian(durationMinutes),
        startTime: logStartTime,
      },
    ],
  };

  const handleSubmit = async (data: DataValues) => {
    const dateChanged = logStartDate !== fromISOtoISODate(data.startDate);
    const originalTime = DateTime.fromISO(actualStart).toFormat('HH:mm');
    const newTime = DateTime.fromISO(data.startDate).toFormat('HH:mm');
    const timeChanged = originalTime !== newTime;

    const values = {
      taskKey: data.taskKey,
      duration: data.duration,
      workLogId: id,
      comment: data.comment,
      startDate: dateChanged || timeChanged ? data.startDate : '',
      usersIds: data.usersIds,
    };

    await onSubmit(values);
  };

  return (
    <TimeLogForm
      open={open}
      onClose={onClose}
      formValues={initialValues}
      onSubmit={handleSubmit}
      onDelete={onDelete}
      isEdit
    />
  );
});

export { EditLog };
